import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { invitePublicApi, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'

type Details = { registryTitle: string; inviterName: string; inviteEmail: string }

type ErrorKind = 'expired' | 'email_mismatch' | 'max_members' | 'unknown'

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const auth = useAuth()
  const nav = useNavigate()

  const [details, setDetails] = useState<Details | null>(null)
  const [error, setError] = useState<ErrorKind | null>(null)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (!token) return
    setError(null)
    setDetails(null)
    invitePublicApi
      .details(token)
      .then(setDetails)
      .catch((e) => setError(e instanceof ApiError && e.status === 404 ? 'expired' : 'unknown'))
  }, [token])

  const nextHref = useMemo(
    () => `/auth/sign-in?next=${encodeURIComponent(`/invites/${token ?? ''}`)}`,
    [token],
  )

  async function accept() {
    if (!token) return
    setAccepting(true)
    setError(null)
    try {
      const r = await invitePublicApi.accept(token)
      nav(`/me/r/${r.registryId}`)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 404) setError('expired')
        else if (e.status === 403) setError('email_mismatch')
        else if (e.status === 409) setError('max_members')
        else setError('unknown')
      } else {
        setError('unknown')
      }
    } finally {
      setAccepting(false)
    }
  }

  if (error === 'expired') {
    return (
      <div className="auth-shell">
        <div className="auth-frame">
          <div className="auth-card">
            <div className="err-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                />
                <path d="M12 7v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r=".9" fill="currentColor" />
              </svg>
            </div>
            <p className="eyebrow">Invitation</p>
            <h1 className="auth-title">Invitation invalide</h1>
            <p className="auth-sub">
              Ce lien a expiré ou n&apos;existe pas. Demandez un nouveau lien à votre invitant.
            </p>
            <div className="auth-actions">
              <button type="button" className="btn-secondary btn-full" onClick={() => nav('/me')}>
                Aller à mes listes
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!details) {
    return (
      <main style={{ padding: '2rem' }} aria-live="polite">
        Chargement…
      </main>
    )
  }

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <div className="auth-card">
          <p className="eyebrow">Invitation</p>
          <h1 className="auth-title">Co-gérer une liste</h1>
          <p className="auth-sub">
            <strong>{details.inviterName}</strong> vous invite à co-gérer la liste «{' '}
            <strong>{details.registryTitle}</strong> ».
          </p>

          <div className="auth-actions">
            {auth.status === 'anonymous' ? (
              <button type="button" className="btn-primary btn-full" onClick={() => nav(nextHref)}>
                Se connecter pour accepter
              </button>
            ) : auth.status === 'authenticated' ? (
              <button
                type="button"
                className="btn-primary btn-full"
                disabled={accepting}
                onClick={() => void accept()}
              >
                {accepting ? (
                  <span className="btn-spin">
                    <span className="mini-spin" aria-hidden="true" /> Acceptation…
                  </span>
                ) : (
                  "Accepter l'invitation"
                )}
              </button>
            ) : null}
          </div>

          {error === 'email_mismatch' && (
            <div role="alert" className="banner banner-error" style={{ marginTop: 16 }}>
              Cette invitation est liée à l&apos;adresse <strong>{details.inviteEmail}</strong>.
              Connectez-vous avec cette adresse pour l&apos;accepter.
            </div>
          )}
          {error === 'max_members' && (
            <div role="alert" className="banner banner-error" style={{ marginTop: 16 }}>
              Cette liste a déjà deux propriétaires. Demandez un nouveau lien à votre invitant.
            </div>
          )}
          {error === 'unknown' && (
            <div role="alert" className="banner banner-error" style={{ marginTop: 16 }}>
              Impossible d&apos;accepter l&apos;invitation. Réessayez dans un instant.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
